import { type User } from '@auth0/auth0-spa-js';
import { captureError } from '@smg-real-estate/browser-rum';

import { UserGroup, type AuthorizedUser } from '@/types';

const userGroupsMap: Map<string, UserGroup> = new Map<string, UserGroup>([
  [UserGroup.PRIVATE.toString(), UserGroup.PRIVATE],
  [UserGroup.AGENCY.toString(), UserGroup.AGENCY],
  [UserGroup.CUSTOMER_SERVICE_AGENT.toString(), UserGroup.CUSTOMER_SERVICE_AGENT],
  [UserGroup.PERSONAL.toString(), UserGroup.PERSONAL],
]);

export function mapGroups(groups: string[] | string): UserGroup[] {
  let values;
  if (typeof groups === 'string') {
    // Replaces e.g. string "['private','ccc']" to array.
    values = groups.split(',');
  } else {
    values = groups;
  }

  return values.reduce((mappedGroups: UserGroup[], group: string) => {
    const mappedGroup = userGroupsMap.get(group);
    return mappedGroup ? [...mappedGroups, mappedGroup] : mappedGroups;
  }, []);
}

export const mapAuth0User = (user?: User): AuthorizedUser | null => {
  if (user) {
    // the auth0 tokens contain the cognito custom claims for backward compatibility.
    // they should in the future be replaced with other claims (e.g. groups could be replaced with permissions)
    return {
      userId: user['cognito:username'],
      email: user.email as string,
      name: user.name,
      picture: user.picture,
      logonId: user.preferred_username as string,
      groups: mapGroups(user['cognito:groups'] || []),
      emailVerified: user.email_verified,
    };
  }
  return null;
};

const decodeAndParseBase64 = (str: string) => JSON.parse(atob(str));

export const parseJwt = (token: string) => {
  try {
    const [header, payload, signature] = token.split('.');
    if (!header || !payload || !signature) {
      throw new Error('Invalid JWT format');
    }

    return {
      header: decodeAndParseBase64(header),
      payload: decodeAndParseBase64(payload),
      signature: signature,
    };
  } catch (error) {
    captureError(error, { service: 'auth', action: 'parse-jwt' });
    return null;
  }
};
